import { px } from "@/components/tool-kit/charts/marks";
import { Arrow } from "@/components/tool-kit/diagrams/svg-parts";
import type { PedigreePerson } from "@/lib/tool-kit/ecology/pedigree";
import { halfHeight, SYMBOL } from "@/lib/tool-kit/ecology/pedigree-layout";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * Pedigree symbols after Bennett et al. (2008): square male, circle female, diamond for
 * sex not specified; filled when affected; a dot (or half fill) for a carrier who will
 * not show the condition; a vertical line for a carrier who may later develop it; a
 * diagonal slash when deceased; an arrow for the proband (with P) or consultand;
 * brackets for an adoption; a diamond with P for a pregnancy and a triangle for one not
 * carried to term.
 */

export type CarrierStyle = "dot" | "half";

export interface SymbolPaint {
  /** Fill of an affected symbol. */
  fill: string;
  carrier: CarrierStyle;
}

const STROKE = 1.8;
const HALF = SYMBOL / 2;

const points = (list: readonly (readonly [number, number])[]) =>
  list.map(([x, y]) => `${px(x)},${px(y)}`).join(" ");

function diamond(x: number, y: number, half: number) {
  return [
    [x, y - half],
    [x + half, y],
    [x, y + half],
    [x - half, y],
  ] as const;
}

function triangle(x: number, y: number, half: number) {
  return [
    [x, y - half],
    [x + half, y + half * 0.8],
    [x - half, y + half * 0.8],
  ] as const;
}

/** The outline, optionally filled. */
function Body({
  person,
  x,
  y,
  fill,
}: {
  person: PedigreePerson;
  x: number;
  y: number;
  fill: string;
}) {
  const common = { fill, stroke: CHART_INK.text, strokeWidth: STROKE };
  if (person.kind === "pregnancy") {
    return <polygon points={points(diamond(x, y, 16))} {...common} />;
  }
  if (person.kind === "miscarriage" || person.kind === "termination") {
    return <polygon points={points(triangle(x, y, 15))} {...common} />;
  }
  if (person.sex === "m") {
    return (
      <rect
        height={SYMBOL}
        width={SYMBOL}
        x={px(x - HALF)}
        y={px(y - HALF)}
        {...common}
      />
    );
  }
  if (person.sex === "f") {
    return <circle cx={px(x)} cy={px(y)} r={HALF} {...common} />;
  }
  return <polygon points={points(diamond(x, y, 22))} {...common} />;
}

/** The left half of the symbol, for a carrier drawn half-filled. */
function HalfFill({
  person,
  x,
  y,
  fill,
}: {
  person: PedigreePerson;
  x: number;
  y: number;
  fill: string;
}) {
  if (person.sex === "m") {
    return (
      <rect
        fill={fill}
        height={SYMBOL}
        width={HALF}
        x={px(x - HALF)}
        y={px(y - HALF)}
      />
    );
  }
  if (person.sex === "f") {
    return (
      <path
        d={`M${px(x)} ${px(y - HALF)}A${HALF} ${HALF} 0 0 0 ${px(x)} ${px(y + HALF)}Z`}
        fill={fill}
      />
    );
  }
  return (
    <polygon
      fill={fill}
      points={points([
        [x, y - 22],
        [x, y + 22],
        [x - 22, y],
      ])}
    />
  );
}

function Decorations({
  person,
  x,
  y,
  paint,
}: {
  person: PedigreePerson;
  x: number;
  y: number;
  paint: SymbolPaint;
}) {
  const half = halfHeight(person);
  const isPerson = person.kind === "person";
  return (
    <g>
      {isPerson && person.status === "carrier" && paint.carrier === "half" ? (
        <HalfFill fill={paint.fill} person={person} x={x} y={y} />
      ) : null}
      {isPerson && person.status === "carrier" && paint.carrier === "dot" ? (
        <circle cx={px(x)} cy={px(y)} fill={paint.fill} r={4.5} />
      ) : null}
      {isPerson && person.status === "presymptomatic" ? (
        <line
          stroke={CHART_INK.text}
          strokeWidth={STROKE}
          x1={px(x)}
          x2={px(x)}
          y1={px(y - half)}
          y2={px(y + half)}
        />
      ) : null}
      {isPerson && person.status === "carrier" && paint.carrier === "half" ? (
        <Body fill="none" person={person} x={x} y={y} />
      ) : null}
    </g>
  );
}

function Brackets({ x, y, half }: { x: number; y: number; half: number }) {
  const top = y - half - 5;
  const bottom = y + half + 5;
  const left = x - half - 9;
  const right = x + half + 9;
  return (
    <path
      d={`M${px(left + 6)} ${px(top)}H${px(left)}V${px(bottom)}H${px(left + 6)}M${px(right - 6)} ${px(top)}H${px(right)}V${px(bottom)}H${px(right - 6)}`}
      fill="none"
      stroke={CHART_INK.text}
      strokeWidth={STROKE}
    />
  );
}

export interface GlyphLabels {
  /** The letter inside a pregnancy symbol. */
  pregnancy: string;
  /** The letter beside the proband's arrow. */
  proband: string;
}

/** One individual: the shape and everything marked on or around it. */
export function PersonGlyph({
  person,
  x,
  y,
  paint,
  labels,
}: {
  person: PedigreePerson;
  x: number;
  y: number;
  paint: SymbolPaint;
  labels: GlyphLabels;
}) {
  const half = halfHeight(person);
  const filled = person.status === "affected";
  const arrow = person.proband || person.consultand;
  return (
    <g>
      <Body
        fill={filled ? paint.fill : CHART_INK.background}
        person={person}
        x={x}
        y={y}
      />
      <Decorations paint={paint} person={person} x={x} y={y} />
      {person.kind === "pregnancy" ? (
        <text
          fill={CHART_INK.text}
          fontSize={13}
          fontWeight="bold"
          textAnchor="middle"
          x={px(x)}
          y={px(y + 4.5)}
        >
          {labels.pregnancy}
        </text>
      ) : null}
      {person.kind === "termination" ? (
        <line
          stroke={CHART_INK.text}
          strokeWidth={STROKE}
          x1={px(x - half - 3)}
          x2={px(x + half + 3)}
          y1={px(y + half + 3)}
          y2={px(y - half - 3)}
        />
      ) : null}
      {person.deceased ? (
        <line
          stroke={CHART_INK.text}
          strokeWidth={STROKE}
          x1={px(x - half - 5)}
          x2={px(x + half + 5)}
          y1={px(y + half + 5)}
          y2={px(y - half - 5)}
        />
      ) : null}
      {person.adopted ? <Brackets half={half} x={x} y={y} /> : null}
      {arrow ? (
        <g>
          <Arrow
            head={8}
            points={[
              [x - half - 28, y + half + 12],
              [x - half - 3, y + half - 3],
            ]}
            width={1.8}
          />
          {person.proband ? (
            <text
              fill={CHART_INK.text}
              fontSize={12}
              fontWeight="bold"
              textAnchor="end"
              x={px(x - half - 32)}
              y={px(y + half + 17)}
            >
              {labels.proband}
            </text>
          ) : null}
        </g>
      ) : null}
    </g>
  );
}
